/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { useProductShelfResults } from './getProductShelfResults';
import { useProductShelfACESResults } from './getProductShelfACESResults';
import { usePageType } from './getPageType';
import { useCategoryResults } from './getCategoryResults';
import { useProductDataTopNavMenuV2 } from '@/features/topNav/api/getProductDataTopNavMenuV2';
import { mapMerchShelfData } from './mapMerchShelfData';
import { mapProductShelfData } from './mapProductShelfData';
import { pageTypes } from '@/constants/page';
import type {
  CategoryData,
  CategoryDataAces,
  ProductShelfACESResponse,
  ProductShelfResponse,
} from '../interface';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { mapACESProductShelfData } from './mapACESProductShelfData';
import { mapACESMerchShelfData } from './mapACESMerchShelfData';
import { type QueryStatus } from '@tanstack/react-query';
import { useMerchShelfResults } from './getMerchShelfResults';

const getCombinedStatus = (
  productShelfStatus: QueryStatus | 'idle',
  categoryResultsStatus: QueryStatus | 'idle',
  isMerchShelfPage: boolean,
) => {
  if (isMerchShelfPage) {
    return productShelfStatus;
  } else if (productShelfStatus === 'success' || categoryResultsStatus === 'success') {
    return 'success';
  } else if (categoryResultsStatus === 'pending' || productShelfStatus === 'pending') {
    return 'pending';
  } else if (categoryResultsStatus === 'error' || productShelfStatus === 'error') {
    return 'error';
  } else {
    return 'idle';
  }
};
const CATEGORY_PAGE_TYPES: unknown[] = [
  pageTypes.TopNavCategory,
  pageTypes.LeafCategory,
  pageTypes.CategoryGrouping,
];

export const useMappedCategoryData = (
  { statusOnly }: { statusOnly: boolean } = { statusOnly: false },
) => {
  const router = useLegacyRouter();
  const { acesEnabled } = useAcesFlags();
  const { data: pageTypeData } = usePageType();

  const isMerchShelfPage = router.route === '/deals/[cmsPath]/[[...slug]]';

  const { data: shelfACESData, status: shelfACESStatus } = useProductShelfACESResults({
    enabled: Boolean(pageTypeData?.pageType) && !isMerchShelfPage && acesEnabled,
  });
  const { data: shelfData, status: shelfStatus } = useProductShelfResults({
    enabled: (Boolean(pageTypeData?.pageType) || isMerchShelfPage) && !acesEnabled,
  });

  const { data: merchShelfData, status: merchShelfStatus } = useMerchShelfResults({
    enabled: isMerchShelfPage && acesEnabled,
  });

  const productShelfData = acesEnabled ? shelfACESData : shelfData;
  const productShelfStatus = acesEnabled
    ? isMerchShelfPage
      ? merchShelfStatus
      : shelfACESStatus
    : shelfStatus;

  const hasCatalogPath =
    Boolean(pageTypeData?.catalogRepositoryIdPath) || Boolean(pageTypeData?.catalogRepositoryId);
  const hasMakeModelYearPath = Boolean(pageTypeData?.makeModelYearPath);

  const shouldUseV2MenuData = acesEnabled && pageTypeData?.pageType === pageTypes.CategoryGrouping;

  const { data: categoryResultsDataV1, status: categoryResultsStatusV1 } = useCategoryResults({
    enabled: Boolean(pageTypeData?.pageType) && !shouldUseV2MenuData,
  });

  const { data: categoryResultsDataV2, status: categoryResultsStatusV2 } =
    useProductDataTopNavMenuV2({
      level: -1,
      categoryIdPath: pageTypeData?.categoryRepositoryIdPath,
      isEnabled: shouldUseV2MenuData && (hasCatalogPath || hasMakeModelYearPath),
    });

  const categoryResultsData = shouldUseV2MenuData ? categoryResultsDataV2 : categoryResultsDataV1;
  const categoryResultsStatus = shouldUseV2MenuData
    ? categoryResultsStatusV2
    : categoryResultsStatusV1;

  const status = getCombinedStatus(productShelfStatus, categoryResultsStatus, isMerchShelfPage);

  const category: CategoryData | undefined | CategoryDataAces = (() => {
    if (statusOnly) {
      return undefined;
    }
    if (isMerchShelfPage) {
      if (acesEnabled && merchShelfData) {
        return {
          ...mapACESMerchShelfData(merchShelfData),
        };
      } else if (productShelfData) {
        return {
          ...mapMerchShelfData(productShelfData as ProductShelfResponse),
        };
      }
    } else if (productShelfData) {
      if (acesEnabled) {
        return {
          ...mapACESProductShelfData(productShelfData as ProductShelfACESResponse, pageTypeData),
        };
      } else {
        return {
          ...mapProductShelfData(productShelfData as ProductShelfResponse, pageTypeData),
        };
      }
    } else if (CATEGORY_PAGE_TYPES.includes(pageTypeData?.pageType) && categoryResultsData) {
      return {
        ...categoryResultsData,
        breadCrumbsList: categoryResultsData.breadCrumbs ?? [],
        make: categoryResultsData.make ?? undefined,
        model: categoryResultsData.model ?? undefined,
        year: categoryResultsData.year ?? undefined,
        productId: null,
        contents: null,
        shopByUrlMake: null,
        shopByUrlModel: null,
        shopByUrlYear: null,
        makeorModelName: null,
        makeModelYearPageType: null,
        redirectPage: categoryResultsData.redirectLocation,
      };
    }
  })();
  const data = status === 'success' ? category : undefined;
  return {
    data,
    status,
    categoryResultsStatus,
  };
};
